import PropTypes from "prop-types";
import useTranslate from "hooks/use-translate-language";
import { PhoneNumberInput, SelectInput, TextInput } from "components/form";
import { FieldArray } from "formik";
import { useSelector } from "react-redux";
import selectors from "selectors";
import { TextField } from "@mui/material";
import Autocomplete from "@mui/lab/Autocomplete";
import { formatDate, isValidPhone, isValidZip } from "utils/misc";
import { STATES } from "utils/enum";

const getInsuranceDisplayName = index => {
  switch (index) {
    case 0:
      return "Primary";
    case 1:
      return "Secondary";
    case 2:
      return "Tertiary";
    default:
      return "";
  }
};

const EditInsurance = ({ values, setFieldValue, errors }) => {
  const translate = useTranslate();
  const companyInsuranceOptions = useSelector(
    selectors.getPatientInsuranceOptions
  );
  const guarantorRelationOptions = useSelector(
    selectors.getGuarantorRelationOptions
  );
  return (
    <div>
      <h4>{translate("insuranceInformation")}</h4>
      <div style={{ marginTop: 20 }}>
        <FieldArray
          name="patient_insurances"
          render={arrayHelpers => (
            <div>
              {values.patient_insurances?.map((_, index) => (
                <div key={index} style={{ padding: 5 }}>
                  <Autocomplete
                    id="insurance-box"
                    getOptionSelected={(option, value) => {
                      return (
                        option?.insurance_company_id ==
                        value?.insurance_company_id
                      );
                    }}
                    options={companyInsuranceOptions.map(x => ({
                      ...x,
                      insurance_company_id: x.value,
                      insurance_company_name: x.text
                    }))}
                    getOptionLabel={option => option.insurance_company_name}
                    value={values.patient_insurances[index]}
                    onChange={(_, newValue) => {
                      setFieldValue(`patient_insurances.${index}`, {
                        ...values.patient_insurances[index],
                        ...newValue,
                        primary_order: index + 1
                      });
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={
                          errors.patient_insurances?.[index]
                            ?.insurance_company_id
                        }
                        helperText={
                          errors.patient_insurances?.[index]
                            ?.insurance_company_id
                        }
                        InputLabelProps={{ shrink: true }}
                        label={getInsuranceDisplayName(index) + " Insurance"}
                        variant="outlined"
                      />
                    )}
                  />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                      alignItems: "center",
                      gridGap: 10,
                      marginTop: 5
                    }}
                  >
                    <TextInput
                      name={`patient_insurances.${index}.plan_no`}
                      type="text"
                      label={translate("policyNumber")}
                    />
                    <TextInput
                      name={`patient_insurances.${index}.group`}
                      type="text"
                      label={translate("groupNumber")}                    />
                    <PhoneNumberInput
                      name={`patient_insurances.${index}.insurance_phone`}
                      type="tel"
                      label={translate("insurancePhoneNumber")}
                      validate={isValidPhone}
                    />
                    <TextInput
                      name={`patient_insurances.${index}.policy_start_date`}
                      type="date"
                      label={translate("policyStartDate")}
                      InputLabelProps={{ shrink: true }}
                      value={formatDate(
                        values.patient_insurances[index].policy_start_date,
                        "YYYY-MM-DD"
                      )}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      alignItems: "center",
                      gridGap: 10,
                      marginTop: 5
                    }}
                  >
                    <TextInput
                      name={`patient_insurances.${index}.guarantor_name`}
                      type="text"
                      label={translate("guarantorName")}
                    />
                    <SelectInput
                      name={`patient_insurances.${index}.guarantor_relation`}
                      component="select"
                      label={translate("guarantorRelation")}
                      options={guarantorRelationOptions}
                      style={{ height: "100%" }} 
                    />
                    <TextInput
                      name={`patient_insurances.${index}.guarantor_dob`}
                      type="date"
                      label={translate("guarantorDob")}
                      InputLabelProps={{ shrink: true }}
                      value={formatDate(
                        values.patient_insurances[index].guarantor_dob,
                        "YYYY-MM-DD"
                      )}
                    />
                  </div>

                  {/*<TextInput
                  name={`insurances.${index}.policy_end_date`}
                  type="date"
                  label="Policy End Date"
                  InputLabelProps={{ shrink: true }}
                  value={formatDate(
                    values.insurances[index].policy_end_date,
                    "YYYY-MM-DD"
                  )}
                  disabled={disabled}
                />*/}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gridGap: 10
                    }}
                  >
                    <TextInput
                      name={`patient_insurances.${index}.insurance_address.street_address`}
                      type="text"
                      label="Insurance Street Address"
                    />

                    <TextInput
                      name={`patient_insurances.${index}.insurance_address.street_address2`}
                      type="text"
                      label="Insurance Street Address 2"
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gridGap: 10
                    }}
                  >
                    <TextInput
                      name={`patient_insurances.${index}.insurance_address.city`}
                      type="text"
                      label="Insurance City"
                    />
                    <SelectInput
                      name={`patient_insurances.${index}.insurance_address.state`}
                      component="select"
                      label="Insurance State"
                      options={STATES}
                      style={{ height: "100%" }} 

                    />
                    <TextInput
                      name={`patient_insurances.${index}.insurance_address.zip`}
                      type="text"
                      label="Insurance Zip"
                      validate={isValidZip}
                    />
                  </div>

                  <div>
                    {index > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flex: 1,
                          justifyContent: "center",
                          alignItems: "center"
                        }}
                      >
                        <button onClick={() => arrayHelpers.remove(index)}>
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                  {index == 0 && <hr style={{ margin: "20px 0px" }} />}
                </div>
              ))}
              {values.patient_insurances?.length == 1 && (
                <div
                  style={{
                    display: "flex",
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  <button
                    type="button"
                    style={{ margin: 0, padding: "5px 15px" }}
                    onClick={() =>
                      arrayHelpers.push({
                        insurance_company_id: "",
                        insurance_company_name: "",
                        primary_order: 2,
                        policy_number: "",
                        group_number: "",
                        guarantor_relation: "",
                        policy_start_date: "",
                        guarantor_dob: "",
                        guarantor_name: "",
                        insurance_phone: "",
                        insurance_address: {
                          street_address: "",
                          street_address2: "",
                          city: "",
                          state: "",
                          zip: ""
                        }
                        //insurance_phone: "",
                        //policy_start_date: "",
                        //policy_end_date: ""
                      })
                    }
                  >
                    Add Secondary Insurance
                  </button>
                </div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

EditInsurance.propTypes = {
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired
};

export default EditInsurance;
